import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import { getXDateFormat } from 'App/Map/PanelDetails/StackemCharts/_utils/by-domain/_utils/getXDateFormat'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import type { StackedChartSection, StackedChartSectionItem } from 'components/StackedChart/types'
import type {
  LegendOptions,
  Point,
  SeriesLineOptions,
  SeriesScatterOptions,
  XAxisPlotLinesOptions,
} from 'highcharts'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { TRGBAColorWithOpacityAtTheEnd } from 'settings/colors'
import { alphaColorReplacer } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'

const sprayColors = ((colors: TRGBAColorWithOpacityAtTheEnd[]) => {
  const lightenedColors = colors.map((rgba) => alphaColorReplacer(rgba, 0.6))

  return colors.concat(lightenedColors)
})([
  'rgba(103, 202, 77, 1)',
  'rgba(240, 146, 54, 1)',
  'rgba(69, 142, 247, 1)',
  'rgba(237, 115, 178, 1)',
  'rgba(174, 91, 205, 1)',
  'rgba(249, 217, 73, 1)',
  'rgba(251, 110, 114, 1)',
  'rgba(53, 154, 32, 1)',
  'rgba(0, 114, 214, 1)',
  'rgba(210, 93, 155, 1)',
  'rgba(218, 152, 241, 1)',
  'rgba(179, 154, 20, 1)',
  'rgba(159, 213, 140, 1)',
  'rgba(249, 185, 133, 1)',
  'rgba(173, 197, 255, 1)',
  'rgba(251, 177, 212, 1)',
  'rgba(232, 180, 248, 1)',
  'rgba(220, 197, 119, 1)',
  'rgba(255, 172, 170, 1)',
])

const computeProductColorMap = ({
  sprayRecords,
  compareSprayRecords,
}: {
  sprayRecords: routes.SprayRecords.Response
  compareSprayRecords: routes.SprayRecords.Response
}) => {
  let uniqueProductNames = new Set<string>()

  for (const block of sprayRecords[0]?.blocks || []) {
    for (const blockSpraysTimeseries of block.sprays.timeseries) {
      for (const product of blockSpraysTimeseries.products) {
        uniqueProductNames.add(product.name)
      }
    }
  }

  for (const block of compareSprayRecords[0]?.blocks || []) {
    for (const blockSpraysTimeseries of block.sprays.timeseries) {
      for (const product of blockSpraysTimeseries.products) {
        uniqueProductNames.add(product.name)
      }
    }
  }

  return Array.from(uniqueProductNames)
    .sort()
    .map((productName, index) => ({
      productName,
      color: sprayColors[index % sprayColors.length],
    }))
}

const makeChart = ({
  sprayRecords,
  titleText,
  timezone,
  selectedBlocks,
  isWideScreen,
  productColorMap,
  compareSeasonsInterval = 0,
}: {
  sprayRecords: routes.SprayRecords.Response
  titleText: string | null
  timezone: string
  selectedBlocks: TOptions[]
  isWideScreen: boolean
  productColorMap: Array<{ productName: string; color: TRGBAColorWithOpacityAtTheEnd }>
  compareSeasonsInterval?: number
}): StackedChartSection[] => {
  const dummySpraySeries: SeriesLineOptions = {
    name: translate.phrases.banyanApp('Spray'),
    showInLegend: true,
    dashStyle: 'ShortDash',
    color: 'rgba(0, 0, 0, 1)',
    type: 'line',
  }

  return selectedBlocks.map((selectedBlock) => {
    const blockSprayRecords = sprayRecords[0]?.blocks?.find(
      (block) => String(block.blockId) === String(selectedBlock.value),
    )

    if (!blockSprayRecords) {
      const noDataChartConfig: StackedChartSectionItem = {
        chartConfig: {
          lang: {
            noData: titleText
              ? translate.phrases.banyanApp('No data found for {{timeRange}}', { timeRange: titleText })
              : translate.phrases.banyanApp('No data found.'),
          },
          chart: {
            type: 'line',
            height: 100,
          },
          tooltip: {
            xDateFormat: getXDateFormat(),
          },
          semiosHighchartsAdditions: {
            id: `spray-records-chart-no-data-${selectedBlock.value}-${titleText}`,
            firstForecastTimestamp: +new Date(),
          },
        },
      }

      return {
        id: `block-${selectedBlock.value}`,
        title: selectedBlock.label as string,
        defaultCollapsed: true,
        items: [noDataChartConfig],
      }
    }

    const sprayRecordsSeries: SeriesScatterOptions[] = []

    blockSprayRecords.sprays.timeseries.forEach((timeseries) => {
      const timestamp = moment
        .tz(timeseries.timestamp, timezone)
        .add(compareSeasonsInterval, 'year')
        .valueOf()

      timeseries.products.forEach((product) => {
        const productSeriesIndex = sprayRecordsSeries.findIndex((d) => d.name === product.name)
        const productColorIndex = productColorMap.findIndex(({ productName }) => productName === product.name)
        const productYValue = productColorMap.length - productColorIndex

        if (productSeriesIndex === -1) {
          const productColor = productColorMap[productColorIndex].color

          sprayRecordsSeries.push({
            name: product.name,
            color: productColor,
            showInLegend: false,
            data: [[timestamp, productYValue]],
            type: 'scatter',
          })
        } else if (
          !sprayRecordsSeries[productSeriesIndex].data?.find((d) => (d as [number, number])[0] === timestamp)
        ) {
          sprayRecordsSeries[productSeriesIndex].data?.push([timestamp, productYValue])
        }
      })
    })

    const tooltipData: Record<number, Record<string, string>> = {}

    blockSprayRecords.sprays.timeseries.forEach((timeseries) => {
      const timestamp = moment
        .tz(timeseries.timestamp, timezone)
        .add(compareSeasonsInterval, 'year')
        .valueOf()

      tooltipData[timestamp] = {}

      timeseries.products.forEach((product) => {
        tooltipData[timestamp][product.name] = product.rate
      })
    })

    const legend: LegendOptions = {
      align: 'left',
    }

    const plotLines: XAxisPlotLinesOptions[] = blockSprayRecords.sprays.timeseries.map((d) => ({
      color: 'rgba(0, 0, 0, 1)',
      value: moment.tz(d.timestamp, timezone).add(compareSeasonsInterval, 'year').valueOf(),
      dashStyle: 'Dash',
      width: 2,
    }))

    let height: number

    if (sprayRecordsSeries.length > 15) {
      height = 800
    } else if (sprayRecordsSeries.length > 8) {
      height = 600
    } else {
      height = 300
    }

    return {
      id: `block-${selectedBlock.value}-${titleText}`,
      title: selectedBlock.label as string,
      items: [
        {
          chartConfig: {
            chart: {
              type: 'line',
              height,
              ...(titleText ? { marginTop: 50 } : {}),
            },
            ...(titleText
              ? {
                  title: {
                    text: titleText,
                    align: 'left',
                    style: {
                      fontWeight: 'bold',
                    },
                  },
                }
              : {}),
            tooltip: {
              xDateFormat: getXDateFormat(),
              formatter: function (): string {
                const point = this as unknown as Point

                const heading = `<div style="margin-bottom: 8px">${translate.dates.format(
                  moment.tz(point.x, timezone).subtract(compareSeasonsInterval, 'year'),
                  'ddd, MMM D, YYYY',
                )}</div>`

                const chemicalList = []

                for (const chemicalName in tooltipData[point.x]) {
                  const rate = tooltipData[point.x][chemicalName]
                  const color = productColorMap.find(({ productName }) => productName === chemicalName)?.color

                  chemicalList.push({ name: chemicalName, color, rate })
                }

                chemicalList.sort(sortByKey('name'))

                const chemicalTableBody = chemicalList
                  .map(
                    ({ name, color, rate }) =>
                      `<tr>
                      <td><span style="background-color: ${color}; display: inline-block; padding: 4px 10px; border-radius: 5px">${name}</span></td>
                      <td>${rate}</td>
                    </tr>`,
                  )
                  .join('')

                return `${heading}<table>${chemicalTableBody}</table>`
              },
            },
            semiosHighchartsAdditions: {
              id: `spray-records-chart-${blockSprayRecords.blockId}-${titleText}`,
              firstForecastTimestamp: +new Date(),
            },
            legend,
            plotOptions: {
              series: {
                states: {
                  hover: {
                    enabled: false,
                  },
                  inactive: {
                    enabled: false,
                  },
                  normal: {
                    animation: false,
                  },
                  select: {
                    enabled: false,
                  },
                },
              },
              scatter: {
                marker: {
                  enabled: false,
                },
                dataLabels: {
                  enabled: true,
                  allowOverlap: true,
                  borderWidth: 1,
                  borderColor: 'rgba(255, 255, 255, 1)',
                  borderRadius: isWideScreen ? 5 : 10,
                  formatter: function (): string {
                    if (!isWideScreen) return ''

                    const point = this as unknown as Point

                    if (point.series.name.length < 11) return point.series.name

                    return `${point.series.name.substring(0, 7)}...`
                  },
                  backgroundColor: 'auto',
                  padding: isWideScreen ? 5 : 10,
                  overflow: 'allow',
                  crop: false,
                },
              },
            },
            xAxis: {
              plotLines,
              tickWidth: 0,
              labels: {
                enabled: false,
              },
            },
            yAxis: {
              labels: {
                enabled: false,
              },
            },
            series: [dummySpraySeries, ...sprayRecordsSeries],
          },
        },
      ],
    }
  })
}

const combineSprayRecordsCharts = (
  chart1: StackedChartSection[],
  chart2: StackedChartSection[],
): StackedChartSection[] => {
  const result = []

  for (let i = 0; i < chart1.length; i++) {
    result.push({
      defaultCollapsed: chart1[i].defaultCollapsed && chart2[i].defaultCollapsed,
      id: chart1[i].id,
      title: chart1[i].title,
      items: [...chart1[i].items, ...chart2[i].items],
    })
  }

  return result
}

export const makeContent = ({
  canViewSprayRecordOnSelectedProperty,
  sprayRecords,
  compareSprayRecords,
  dateFrom,
  dateTo,
  compareSeasonsInterval,
  timezone,
  selectedBlocks,
  isWideScreen,
  propertyId,
}: {
  canViewSprayRecordOnSelectedProperty: boolean
  sprayRecords: routes.SprayRecords.Response
  compareSprayRecords: routes.SprayRecords.Response
  dateFrom: string
  dateTo: string
  compareSeasonsInterval: number
  timezone: string
  selectedBlocks: TOptions[]
  isWideScreen: boolean
  propertyId: number
}): StackedChartSection[] => {
  if (!propertyId) {
    const noPropertySelectedChartConfig: StackedChartSectionItem = {
      chartConfig: {
        lang: {
          noData: translate.phrases.banyanApp('Please select a property'),
        },
        chart: {
          type: 'line',
        },
        tooltip: {
          xDateFormat: getXDateFormat(),
        },
        semiosHighchartsAdditions: {
          id: 'spray-records-chart-no-property',
          firstForecastTimestamp: +new Date(),
        },
      },
    }

    return [
      {
        id: 'no-property',
        title: '',
        defaultCollapsed: false,
        items: [noPropertySelectedChartConfig],
      },
    ]
  }

  if (!canViewSprayRecordOnSelectedProperty) {
    const noPermissionChartConfig: StackedChartSectionItem = {
      chartConfig: {
        lang: {
          noData: translate.phrases.banyanApp('You do not have access to this feature for {{propertyName}}', {
            propertyName: fieldAssetStore.getState().properties?.[propertyId]?.propertyName,
          }),
        },
        chart: {
          type: 'line',
        },
        tooltip: {
          xDateFormat: getXDateFormat(),
        },
        semiosHighchartsAdditions: {
          id: 'spray-records-chart-no-permission',
          firstForecastTimestamp: +new Date(),
        },
      },
    }

    return [
      {
        id: 'no-permission',
        title: '',
        defaultCollapsed: false,
        items: [noPermissionChartConfig],
      },
    ]
  }

  if (selectedBlocks.length === 0) {
    const noBlockSelectedChartConfig: StackedChartSectionItem = {
      chartConfig: {
        lang: {
          noData: translate.phrases.banyanApp('No data found. Please select a Block.'),
        },
        chart: {
          type: 'line',
        },
        tooltip: {
          xDateFormat: getXDateFormat(),
        },
        semiosHighchartsAdditions: {
          id: 'spray-records-chart-no-data',
          firstForecastTimestamp: +new Date(),
        },
      },
    }

    return [
      {
        id: 'block-empty',
        title: '',
        defaultCollapsed: true,
        items: [noBlockSelectedChartConfig],
      },
    ]
  }

  const productColorMap = computeProductColorMap({ sprayRecords, compareSprayRecords })

  if (!compareSeasonsInterval) {
    return makeChart({
      sprayRecords,
      titleText: null,
      timezone,
      selectedBlocks,
      isWideScreen,
      productColorMap,
    })
  }

  const dateRangeSameYear = moment.tz(dateFrom, timezone).isSame(dateTo, 'year')

  const titleText = translate.phrases.templates('{{dateFrom}} - {{dateTo}}', {
    dateFrom: translate.dates.format(
      moment.tz(dateFrom, timezone),
      dateRangeSameYear ? 'MMM D' : 'MMM D, YYYY',
    ),
    dateTo: translate.dates.format(moment.tz(dateTo, timezone), 'MMM D, YYYY'),
  })

  const sprayRecordsChart = makeChart({
    sprayRecords,
    titleText,
    timezone,
    selectedBlocks,
    isWideScreen,
    productColorMap,
  })

  const compareDateFrom = moment.tz(dateFrom, timezone).subtract(compareSeasonsInterval, 'year')
  const compareDateTo = moment.tz(dateTo, timezone).subtract(compareSeasonsInterval, 'year')

  const compareTitleText = translate.phrases.templates('{{dateFrom}} - {{dateTo}}', {
    dateFrom: translate.dates.format(
      moment.tz(compareDateFrom, timezone),
      dateRangeSameYear ? 'MMM D' : 'MMM D, YYYY',
    ),
    dateTo: translate.dates.format(moment.tz(compareDateTo, timezone), 'MMM D, YYYY'),
  })

  const compareSprayRecordsChart = makeChart({
    sprayRecords: compareSprayRecords,
    titleText: compareTitleText,
    timezone,
    selectedBlocks,
    isWideScreen,
    productColorMap,
    compareSeasonsInterval,
  })

  return combineSprayRecordsCharts(sprayRecordsChart, compareSprayRecordsChart)
}
